import React from 'react';
import { Package, Globe, Award, TrendingUp, Users, Zap, Target, BarChart } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: Globe,
      title: 'International Trading',
      description: 'Export premium Basmati rice to markets worldwide, with special expertise in GCC countries.',
      features: ['Worldwide Shipping', 'Custom Documentation', 'Quality Assurance', 'Timely Delivery']
    },
    {
      icon: Package,
      title: 'Premium Packing',
      description: 'State-of-the-art packaging under our flagship brand Amyrah, ensuring freshness and quality in every pack.',
      features: ['Hygienic Processing', 'Moisture Control', 'Premium Packaging', 'Quality Sealing']
    },
    {
      icon: Users,
      title: 'Private Labeling',
      description: 'Custom branding solutions for businesses looking to launch their own premium Basmati rice brand.',
      features: ['Custom Branding', 'Low Minimums', 'Design Support', 'Market Insights']
    },
    {
      icon: Award,
      title: 'Quality Assurance',
      description: 'Rigorous quality control at every stage, from farm to fork, ensuring the finest Basmati rice.',
      features: ['Lab Testing', 'Grain Analysis', 'Purity Checks', 'International Standards']
    },
  ];

  const privateLabelingBenefits = [
    { icon: Award, title: 'Unmatched Quality', description: 'Premium Basmati rice sourced from the finest farms' },
    { icon: BarChart, title: 'Product Marketing Insights', description: 'Data-driven strategies for market success' },
    { icon: TrendingUp, title: 'Expansion Assistance', description: 'Support for scaling your business' },
    { icon: Package, title: 'Low Minimums', description: 'Flexible order quantities to suit your needs' },
    { icon: Zap, title: 'Faster Turnaround Time', description: 'Quick processing and delivery' },
    { icon: Target, title: 'Portfolio Diversification', description: 'Expand your product range effortlessly' },
    { icon: Users, title: 'Industry Trend Expertise', description: 'Stay ahead with market intelligence' },
    { icon: Globe, title: 'Diverse Production Capabilities', description: 'Multiple varieties and packaging options' },
  ];

  return (
    <div className="min-h-screen" data-testid="services-page">
      {/* Hero Section */}
      <section 
        className="relative h-[60vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1764842344473-02930cc67bbc')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">Services</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Comprehensive solutions for all your Basmati rice needs
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What We <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">Offer</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              End-to-end solutions for premium Basmati rice trading and packaging
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-amber-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-amber-100"
                >
                  <div className="inline-flex p-4 bg-gradient-to-r from-amber-600 to-yellow-500 rounded-full mb-6">
                    <Icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-amber-500 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Processing Capacity */}
      <section className="py-20 bg-gradient-to-br from-amber-600 via-yellow-500 to-amber-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-6">Our Processing Power</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl">
                <p className="text-5xl font-bold mb-2">300</p>
                <p className="text-xl">Metric Tons Per Day</p>
                <p className="text-sm text-amber-50 mt-2">Processing Capacity</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl">
                <p className="text-5xl font-bold mb-2">5000+</p>
                <p className="text-xl">Metric Tons Annually</p>
                <p className="text-sm text-amber-50 mt-2">Domestic Sales</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl">
                <p className="text-5xl font-bold mb-2">PAN</p>
                <p className="text-xl">India Presence</p>
                <p className="text-sm text-amber-50 mt-2">Under Amyrah Brand</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Private Labeling Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Private Labeling &
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">
                Customized Packing Services
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Launch your own premium Basmati rice brand with our comprehensive support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {privateLabelingBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="text-center p-6 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  <div className="inline-flex p-3 bg-gradient-to-r from-amber-600 to-yellow-500 rounded-full mb-4">
                    <Icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Quality
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">
                  Assurance Process
                </span>
              </h2>
              <div className="space-y-6">
                <ProcessStep 
                  number="01" 
                  title="Sourcing" 
                  description="Carefully selecting premium Basmati rice from trusted farmers"
                />
                <ProcessStep 
                  number="02" 
                  title="Processing" 
                  description="State-of-the-art facilities with 300 MT daily capacity"
                />
                <ProcessStep 
                  number="03" 
                  title="Quality Testing" 
                  description="Rigorous lab tests for grain length, aroma, and purity"
                />
                <ProcessStep 
                  number="04" 
                  title="Packaging" 
                  description="Hygienic packing ensuring freshness and quality"
                />
                <ProcessStep 
                  number="05" 
                  title="Distribution" 
                  description="Timely delivery to markets across India and globally"
                />
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1764675902777-6c0c0ac16f61" 
                alt="Processing Facility" 
                className="rounded-2xl shadow-2xl w-full h-[600px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Experience premium quality Basmati rice and exceptional service
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-500 text-white rounded-full font-semibold text-lg hover:from-amber-500 hover:to-yellow-400 transition-all duration-300 shadow-xl"
              data-testid="request-quote-btn"
            >
              Request a Quote
            </a>
            <a 
              href="https://wa.me/919810137856" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-4 bg-green-600 text-white rounded-full font-semibold text-lg hover:bg-green-700 transition-all duration-300 shadow-xl"
              data-testid="whatsapp-contact-btn"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const ProcessStep = ({ number, title, description }) => {
  return (
    <div className="flex items-start space-x-4">
      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-amber-600 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
        {number}
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export default Services;